import type { Component } from '@/types';
import type { AICommandMutationCategory } from '@/services/ai/agentCommandPermissions';
import { getMutationCategoriesForChanges } from '@/services/ai/agentCommandPermissions';
import type { SlashCommandResult } from '@/services/ai/commands/useSlashCommands';

export function getSlashCommandMutationCategories(
  result: SlashCommandResult,
  options: { isCanvasMutation?: boolean } = {},
): AICommandMutationCategory[] {
  const categories = new Set<AICommandMutationCategory>(
    getMutationCategoriesForChanges(result.propertyChanges, options),
  );

  for (const mutation of result.plannedMutations ?? []) {
    if (mutation.type === 'addComponent' || mutation.type === 'addComponents') {
      categories.add('insertion');
      continue;
    }
    if (mutation.type === 'deleteComponent') {
      categories.add('delete');
      continue;
    }
    for (const category of getMutationCategoriesForChanges(mutation.changes)) {
      categories.add(category);
    }
  }

  return [...categories];
}

export function getSlashCommandMutationTargets(
  result: SlashCommandResult,
  primaryTarget: Component | null | undefined,
): {
  targetComponents: Component[];
  targetComponentIds: string[];
} {
  const targetComponents: Component[] = [];
  const targetComponentIds = new Set<string>();

  const addTarget = (target: Component | null | undefined) => {
    if (!target) {
      return;
    }
    if (!targetComponents.some((component) => component.id === target.id)) {
      targetComponents.push(target);
    }
    targetComponentIds.add(target.id);
  };

  addTarget(primaryTarget);

  for (const mutation of result.plannedMutations ?? []) {
    if ('targetComponent' in mutation) {
      addTarget(mutation.targetComponent);
    }
    if ('componentId' in mutation) {
      targetComponentIds.add(mutation.componentId);
    }
  }

  return {
    targetComponents,
    targetComponentIds: [...targetComponentIds],
  };
}
